import React, { useState } from 'react';
import { ChevronDown, ChevronRight, TrendingUp, Star, AlertCircle } from 'lucide-react';
import SportsbookLogo, { SPORTSBOOK_NAMES } from './SportsbookLogo';
import TeamLogo from './TeamLogo';
import { useCart } from '../context/CartContext';

// Market display order by sport
const PROP_MARKET_ORDER_BY_SPORT = {
  basketball_nba: ['player_points', 'player_rebounds', 'player_assists', 'player_threes', 'player_points_rebounds_assists', 'player_steals', 'player_blocks'],
  icehockey_nhl: ['player_points', 'player_goals', 'player_assists', 'player_shots_on_goal', 'player_saves'],
  baseball_mlb: ['batter_hits', 'batter_total_bases', 'batter_rbis', 'batter_runs_scored', 'batter_home_runs', 'pitcher_strikeouts', 'pitcher_outs'],
};

// Display names for all prop markets (matches backend Config.PROP_MARKET_DISPLAY_NAMES)
const PROP_MARKET_LABELS = {
  // NBA
  player_points: 'Points',
  player_rebounds: 'Rebounds',
  player_assists: 'Assists',
  player_threes: '3-Pointers',
  player_points_rebounds_assists: 'PRA',
  player_steals: 'Steals',
  player_blocks: 'Blocks',
  // NHL
  player_goals: 'Goals',
  player_shots_on_goal: 'Shots',
  player_saves: 'Saves',
  // MLB
  batter_hits: 'Hits',
  batter_total_bases: 'Total Bases',
  batter_rbis: 'RBIs',
  batter_runs_scored: 'Runs',
  batter_home_runs: 'Home Runs',
  pitcher_strikeouts: 'Strikeouts',
  pitcher_outs: 'Outs',
};

// Default fallback order (NBA)
const PROP_MARKET_ORDER = PROP_MARKET_ORDER_BY_SPORT.basketball_nba;

function formatOdds(odds) {
  if (odds === null || odds === undefined) return '-';
  return odds > 0 ? `+${odds}` : `${odds}`;
}

function PropPlayerCard({ playerName, markets, bestOdds, evProps, arbProps = [], sportKey = 'basketball_nba', bankroll = 1000, kellyFraction = 0.25, session, onTrackProp, selectedBooks = [], eventId, eventName, playerInfo = {} }) {
  const [expanded, setExpanded] = useState(false);
  const [activeMarket, setActiveMarket] = useState(null);
  const [trackingKey, setTrackingKey] = useState(null); // "{marketKey}-{point}-{side}"
  const [trackAmount, setTrackAmount] = useState('');
  const [trackedKeys, setTrackedKeys] = useState(new Set());
  const { addSelection, removeSelection, selections } = useCart();

  function handleTrack(marketKey, point, side, odds, bookKey) {
    const key = `${marketKey}-${point}-${side}`;
    if (!onTrackProp) return;
    onTrackProp({ playerName, propType: marketKey, point: parseFloat(point), side, odds, bookKey, amount: parseFloat(trackAmount) || 0 });
    setTrackedKeys(prev => { const next = new Set(prev); next.add(key); return next; });
    setTrackingKey(null);
    setTrackAmount('');
  }

  // Get EV props for this player
  const playerEvProps = evProps.filter(ev => ev.player === playerName);
  const hasEv = playerEvProps.length > 0;

  // Get arb props for this player
  const playerArbProps = arbProps.filter(arb => arb.player === playerName);
  const hasArb = playerArbProps.length > 0;

  // Get available markets for this player in preferred order (sport-specific)
  const marketOrder = PROP_MARKET_ORDER_BY_SPORT[sportKey] || PROP_MARKET_ORDER;
  const availableMarkets = marketOrder.filter(m => markets[m]);

  if (availableMarkets.length === 0) return null;

  return (
    <div className={`prop-player-card ${hasEv ? 'has-ev' : ''} ${hasArb ? 'has-arb' : ''}`}>
      <button className="prop-player-header" onClick={() => setExpanded(!expanded)}>
        <div className="prop-player-name">
          <div className="prop-player-name-row">
            {playerName}
            {hasEv && (
              <span className="prop-ev-count">
                <TrendingUp size={12} />
                {playerEvProps.length} +EV
              </span>
            )}
            {hasArb && (
              <span className="prop-arb-count">
                <AlertCircle size={12} />
                {playerArbProps.length} Arb
              </span>
            )}
          </div>
          {playerInfo[playerName] && (
            <div className="prop-player-meta">
              {playerInfo[playerName].jersey && <span className="prop-player-jersey">#{playerInfo[playerName].jersey}</span>}
              {playerInfo[playerName].team && (
                <>
                  <TeamLogo teamName={playerInfo[playerName].team} sportKey={sportKey} size={14} />
                  <span className="prop-player-team">{playerInfo[playerName].team}</span>
                </>
              )}
            </div>
          )}
        </div>
        <div className="prop-player-toggle">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </button>

      {expanded && (
        <div className="prop-player-markets">
          {/* Stat tabs */}
          <div className="prop-stat-tabs">
            {availableMarkets.map(marketKey => {
              const hasEv = playerEvProps.some(ev => ev.market === marketKey);
              const hasArb = playerArbProps.some(arb => arb.prop_type === marketKey);
              const selected = (activeMarket || availableMarkets[0]) === marketKey;
              return (
                <button
                  key={marketKey}
                  className={`prop-stat-tab ${selected ? 'active' : ''} ${hasEv ? 'has-ev' : ''} ${hasArb ? 'has-arb' : ''}`}
                  onClick={() => setActiveMarket(marketKey)}
                >
                  {PROP_MARKET_LABELS[marketKey] || marketKey}
                  {hasEv && <span className="prop-stat-tab-dot ev-dot" />}
                  {hasArb && <span className="prop-stat-tab-dot arb-dot" />}
                </button>
              );
            })}
          </div>

          <div className="prop-table">
            <div className="prop-table-header">
              <div className="prop-col-line">Line</div>
              <div className="prop-col-odds">Best Over</div>
              <div className="prop-col-odds">Best Under</div>
            </div>

            {availableMarkets.filter(m => m === (activeMarket || availableMarkets[0])).map(marketKey => {
              const lines = markets[marketKey];

              // Get all lines for this market (usually just one per player/market)
              return Object.entries(lines).map(([point, books]) => {
                // Filter books by user's selection; fall back to all books if none selected
                const visibleBooks = selectedBooks.length > 0
                  ? Object.fromEntries(Object.entries(books).filter(([k]) => selectedBooks.includes(k)))
                  : books;
                const overBestEntry = Object.entries(visibleBooks)
                  .filter(([, o]) => o.over != null)
                  .reduce((best, [bk, o]) => (!best || o.over > best.odds) ? { book: bk, odds: o.over } : best, null);
                const underBestEntry = Object.entries(visibleBooks)
                  .filter(([, o]) => o.under != null)
                  .reduce((best, [bk, o]) => (!best || o.under > best.odds) ? { book: bk, odds: o.under } : best, null);
                const overBest = overBestEntry ? { book: overBestEntry.book, odds: overBestEntry.odds } : {};
                const underBest = underBestEntry ? { book: underBestEntry.book, odds: underBestEntry.odds } : {};

                // Check if this line has +EV
                const evOver = playerEvProps.find(
                  ev => ev.market === marketKey && ev.line === parseFloat(point) && ev.side === 'Over'
                );
                const evUnder = playerEvProps.find(
                  ev => ev.market === marketKey && ev.line === parseFloat(point) && ev.side === 'Under'
                );

                // Check if this line has arb opportunity
                const arbOpp = playerArbProps.find(
                  arb => arb.prop_type === marketKey && arb.line === parseFloat(point)
                );

                return (
                  <div key={`${marketKey}-${point}`} className={`prop-table-row ${arbOpp ? 'has-arb' : ''}`}>
                    <div className="prop-col-line">
                      {point}
                      {arbOpp && (
                        <span className="prop-arb-badge" title={`${arbOpp.profit_percentage}% profit`}>
                          <AlertCircle size={10} />
                          {arbOpp.profit_percentage}%
                        </span>
                      )}
                    </div>
                    <div className={`prop-col-odds ${evOver ? 'ev-highlight' : ''} ${arbOpp ? 'arb-highlight' : ''}`}>
                      <div className="prop-odds-cell">
                        <span className="prop-odds-value">{formatOdds(overBest.odds)}</span>
                        {overBest.book && (
                          <div className="prop-odds-book">
                            <SportsbookLogo bookKey={overBest.book} size={14} />
                            <span className="prop-book-abbrev">
                              {SPORTSBOOK_NAMES[overBest.book]?.slice(0, 3) || overBest.book.slice(0, 3)}
                            </span>
                          </div>
                        )}
                        {evOver && (
                          <span className="prop-ev-badge">
                            +{evOver.ev_percentage}%
                            {evOver.kelly_fraction > 0 && bankroll > 0 && (
                              <span className="prop-kelly-bet">
                                ${Math.round(evOver.kelly_fraction * kellyFraction * bankroll)}
                              </span>
                            )}
                          </span>
                        )}
                        {(() => {
                          const overByBook = Object.fromEntries(
                            Object.entries(visibleBooks).filter(([, o]) => o.over != null).map(([bk, o]) => [bk, o.over])
                          );
                          const cartTeam = `${playerName} Over ${point}`;
                          const inCart = selections.some(s => s.eventId === eventId && s.team === cartTeam && s.market === marketKey);
                          const cartSel = selections.find(s => s.eventId === eventId && s.team === cartTeam && s.market === marketKey);
                          const key = `${marketKey}-${point}-Over`;
                          const isTracking = trackingKey === key;
                          const isTracked = trackedKeys.has(key);
                          if (isTracking) return (
                            <div className="prop-track-form" onClick={e => e.stopPropagation()}>
                              <input type="number" className="prop-track-input" placeholder="$" value={trackAmount} onChange={e => setTrackAmount(e.target.value)} autoFocus min="0" step="1" />
                              <button className="prop-track-save" onClick={() => handleTrack(marketKey, point, 'Over', overBest.odds, overBest.book)}>✓</button>
                              <button className="prop-track-cancel" onClick={() => { setTrackingKey(null); setTrackAmount(''); }}>✕</button>
                              {parseFloat(trackAmount) > 0 && overBest.odds && <span className="ctf-to-win">To win: +${(parseFloat(trackAmount) * (overBest.odds > 0 ? overBest.odds / 100 : 100 / Math.abs(overBest.odds))).toFixed(2)}</span>}
                            </div>
                          );
                          return (
                            <div className="compact-cell-actions">
                              <button
                                className={`cca-parlay${inCart ? ' selected' : ''}`}
                                onClick={e => {
                                  e.stopPropagation();
                                  if (inCart) { removeSelection(cartSel.id); return; }
                                  if (!overBest.odds) return;
                                  addSelection({ eventId, eventName, team: cartTeam, market: marketKey, point: parseFloat(point), odds: overBest.odds, oddsAllBooks: overByBook, selected_odds: overBest.odds, sportKey });
                                }}
                              >{inCart ? '✓ Parlay' : '+ Parlay'}</button>
                              {session && overBest.odds && overBest.book && (
                                isTracked
                                  ? <span className="cca-tracked-label">✓</span>
                                  : <button className="cca-track" onClick={e => { e.stopPropagation(); setTrackingKey(key); setTrackAmount(''); }}>Track</button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    <div className={`prop-col-odds ${evUnder ? 'ev-highlight' : ''} ${arbOpp ? 'arb-highlight' : ''}`}>
                      <div className="prop-odds-cell">
                        <span className="prop-odds-value">{formatOdds(underBest.odds)}</span>
                        {underBest.book && (
                          <div className="prop-odds-book">
                            <SportsbookLogo bookKey={underBest.book} size={14} />
                            <span className="prop-book-abbrev">
                              {SPORTSBOOK_NAMES[underBest.book]?.slice(0, 3) || underBest.book.slice(0, 3)}
                            </span>
                          </div>
                        )}
                        {evUnder && (
                          <span className="prop-ev-badge">
                            +{evUnder.ev_percentage}%
                            {evUnder.kelly_fraction > 0 && bankroll > 0 && (
                              <span className="prop-kelly-bet">
                                ${Math.round(evUnder.kelly_fraction * kellyFraction * bankroll)}
                              </span>
                            )}
                          </span>
                        )}
                        {(() => {
                          const underByBook = Object.fromEntries(
                            Object.entries(visibleBooks).filter(([, o]) => o.under != null).map(([bk, o]) => [bk, o.under])
                          );
                          const cartTeam = `${playerName} Under ${point}`;
                          const inCart = selections.some(s => s.eventId === eventId && s.team === cartTeam && s.market === marketKey);
                          const cartSel = selections.find(s => s.eventId === eventId && s.team === cartTeam && s.market === marketKey);
                          const key = `${marketKey}-${point}-Under`;
                          const isTracking = trackingKey === key;
                          const isTracked = trackedKeys.has(key);
                          if (isTracking) return (
                            <div className="prop-track-form" onClick={e => e.stopPropagation()}>
                              <input type="number" className="prop-track-input" placeholder="$" value={trackAmount} onChange={e => setTrackAmount(e.target.value)} autoFocus min="0" step="1" />
                              <button className="prop-track-save" onClick={() => handleTrack(marketKey, point, 'Under', underBest.odds, underBest.book)}>✓</button>
                              <button className="prop-track-cancel" onClick={() => { setTrackingKey(null); setTrackAmount(''); }}>✕</button>
                              {parseFloat(trackAmount) > 0 && underBest.odds && <span className="ctf-to-win">To win: +${(parseFloat(trackAmount) * (underBest.odds > 0 ? underBest.odds / 100 : 100 / Math.abs(underBest.odds))).toFixed(2)}</span>}
                            </div>
                          );
                          return (
                            <div className="compact-cell-actions">
                              <button
                                className={`cca-parlay${inCart ? ' selected' : ''}`}
                                onClick={e => {
                                  e.stopPropagation();
                                  if (inCart) { removeSelection(cartSel.id); return; }
                                  if (!underBest.odds) return;
                                  addSelection({ eventId, eventName, team: cartTeam, market: marketKey, point: parseFloat(point), odds: underBest.odds, oddsAllBooks: underByBook, selected_odds: underBest.odds, sportKey });
                                }}
                              >{inCart ? '✓ Parlay' : '+ Parlay'}</button>
                              {session && underBest.odds && underBest.book && (
                                isTracked
                                  ? <span className="cca-tracked-label">✓</span>
                                  : <button className="cca-track" onClick={e => { e.stopPropagation(); setTrackingKey(key); setTrackAmount(''); }}>Track</button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              });
            })}
          </div>

          {/* Expandable section showing all books */}
          <PropAllBooks playerName={playerName} markets={markets} bestOdds={bestOdds} sportKey={sportKey} selectedBooks={selectedBooks} session={session} onTrackProp={onTrackProp} eventId={eventId} eventName={eventName} />
        </div>
      )}
    </div>
  );
}

function PropAllBooks({ playerName, markets, bestOdds, sportKey = 'basketball_nba', selectedBooks = [], session, onTrackProp, eventId, eventName }) {
  const [showAll, setShowAll] = useState(false);
  const [trackingKey, setTrackingKey] = useState(null); // `${marketKey}-${point}-${bookKey}-${side}`
  const [trackAmount, setTrackAmount] = useState('');
  const [trackedKeys, setTrackedKeys] = useState(new Set());
  const marketOrder = PROP_MARKET_ORDER_BY_SPORT[sportKey] || PROP_MARKET_ORDER;

  function handleTrack(marketKey, point, side, odds, bookKey) {
    const key = `${marketKey}-${point}-${bookKey}-${side}`;
    if (!onTrackProp) return;
    onTrackProp({ playerName, propType: marketKey, point: parseFloat(point), side, odds, bookKey, amount: parseFloat(trackAmount) || 0 });
    setTrackedKeys(prev => { const next = new Set(prev); next.add(key); return next; });
    setTrackingKey(null);
    setTrackAmount('');
  }

  return (
    <div className="prop-all-books">
      <button className="prop-show-all-toggle" onClick={() => setShowAll(!showAll)}>
        {showAll ? 'Hide all sportsbooks' : 'Show all sportsbooks'}
        {showAll ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>

      {showAll && (
        <div className="prop-all-books-grid">
          {marketOrder.filter(m => markets[m]).map(marketKey => {
            const lines = markets[marketKey];
            const marketBest = bestOdds?.[marketKey] || {};

            return Object.entries(lines).map(([point, books]) => {
              const lineBest = marketBest[point] || {};
              const visibleBooks = selectedBooks.length > 0
                ? Object.entries(books).filter(([k]) => selectedBooks.includes(k))
                : Object.entries(books);

              if (visibleBooks.length === 0) return null;

              return (
                <div key={`${marketKey}-${point}`} className="prop-all-books-section">
                  <div className="prop-all-books-header">
                    {PROP_MARKET_LABELS[marketKey]} - {point}
                  </div>
                  <div className="prop-all-books-table">
                    <div className="prop-all-books-row header">
                      <div className="prop-all-col-book">Book</div>
                      <div className="prop-all-col-odds">Over</div>
                      <div className="prop-all-col-odds">Under</div>
                    </div>
                    {visibleBooks.map(([bookKey, odds]) => (
                      <div key={bookKey} className="prop-all-books-row">
                        <div className="prop-all-col-book">
                          <SportsbookLogo bookKey={bookKey} size={16} />
                          <span>{SPORTSBOOK_NAMES[bookKey] || bookKey}</span>
                        </div>
                        <div className={`prop-all-col-odds ${lineBest.over?.book === bookKey ? 'best-odds' : ''}`}>
                          {odds.over != null && (
                            <>
                              <span>{formatOdds(odds.over)}</span>
                              {lineBest.over?.book === bookKey && <Star size={10} className="best-star" />}
                              {session && (() => {
                                const key = `${marketKey}-${point}-${bookKey}-Over`;
                                const isTracking = trackingKey === key;
                                const isTracked = trackedKeys.has(key);
                                if (isTracking) return (
                                  <div className="pab-track-form" onClick={e => e.stopPropagation()}>
                                    <input type="number" className="prop-track-input" placeholder="$" value={trackAmount} onChange={e => setTrackAmount(e.target.value)} autoFocus min="0" step="1" />
                                    <button className="prop-track-save" onClick={() => handleTrack(marketKey, point, 'Over', odds.over, bookKey)}>✓</button>
                                    <button className="prop-track-cancel" onClick={() => { setTrackingKey(null); setTrackAmount(''); }}>✕</button>
                                  </div>
                                );
                                return isTracked
                                  ? <span className="pab-tracked">✓</span>
                                  : <button className="pab-track-btn" onClick={() => { setTrackingKey(key); setTrackAmount(''); }}>Track</button>;
                              })()}
                            </>
                          )}
                        </div>
                        <div className={`prop-all-col-odds ${lineBest.under?.book === bookKey ? 'best-odds' : ''}`}>
                          {odds.under != null && (
                            <>
                              <span>{formatOdds(odds.under)}</span>
                              {lineBest.under?.book === bookKey && <Star size={10} className="best-star" />}
                              {session && (() => {
                                const key = `${marketKey}-${point}-${bookKey}-Under`;
                                const isTracking = trackingKey === key;
                                const isTracked = trackedKeys.has(key);
                                if (isTracking) return (
                                  <div className="pab-track-form" onClick={e => e.stopPropagation()}>
                                    <input type="number" className="prop-track-input" placeholder="$" value={trackAmount} onChange={e => setTrackAmount(e.target.value)} autoFocus min="0" step="1" />
                                    <button className="prop-track-save" onClick={() => handleTrack(marketKey, point, 'Under', odds.under, bookKey)}>✓</button>
                                    <button className="prop-track-cancel" onClick={() => { setTrackingKey(null); setTrackAmount(''); }}>✕</button>
                                  </div>
                                );
                                return isTracked
                                  ? <span className="pab-tracked">✓</span>
                                  : <button className="pab-track-btn" onClick={() => { setTrackingKey(key); setTrackAmount(''); }}>Track</button>;
                              })()}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            });
          })}
        </div>
      )}
    </div>
  );
}

function PropsSection({ props, evProps, arbProps = [], bestOdds, loading, error, sportKey = 'basketball_nba', bankroll = 1000, kellyFraction = 0.25, session, onTrackProp, selectedBooks = [], eventId, eventName, playerInfo = {} }) {
  if (loading) {
    return (
      <div className="props-section">
        <div className="props-section-header">
          <h3>Player Props</h3>
        </div>
        <div className="props-loading">Loading player props...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="props-section">
        <div className="props-section-header">
          <h3>Player Props</h3>
        </div>
        <div className="props-error">{error}</div>
      </div>
    );
  }

  if (!props || Object.keys(props).length === 0) {
    return (
      <div className="props-section">
        <div className="props-section-header">
          <h3>Player Props</h3>
        </div>
        <div className="props-empty">No player props available for this game</div>
      </div>
    );
  }

  // Sort players: those with +EV or arb props first, then alphabetically
  const playerNames = Object.keys(props).sort((a, b) => {
    const aHasEv = evProps.some(ev => ev.player === a);
    const bHasEv = evProps.some(ev => ev.player === b);
    const aHasArb = arbProps.some(arb => arb.player === a);
    const bHasArb = arbProps.some(arb => arb.player === b);
    const aHasOpp = aHasEv || aHasArb;
    const bHasOpp = bHasEv || bHasArb;
    if (aHasOpp && !bHasOpp) return -1;
    if (!aHasOpp && bHasOpp) return 1;
    return a.localeCompare(b);
  });

  const displayPlayers = playerNames;

  const evCount = evProps.length;
  const arbCount = arbProps.length;

  return (
    <div className="props-section">
      <div className="props-section-header">
        <h3>
          Player Props
          {evCount > 0 && (
            <span className="props-ev-summary">
              <TrendingUp size={14} />
              {evCount} +EV
            </span>
          )}
          {arbCount > 0 && (
            <span className="props-arb-summary">
              <AlertCircle size={14} />
              {arbCount} Arb
            </span>
          )}
        </h3>
      </div>

      <div className="props-players-list">
        {displayPlayers.map(playerName => (
          <PropPlayerCard
            key={playerName}
            playerName={playerName}
            markets={props[playerName]}
            bestOdds={bestOdds?.[playerName]}
            evProps={evProps}
            arbProps={arbProps}
            sportKey={sportKey}
            bankroll={bankroll}
            kellyFraction={kellyFraction}
            session={session}
            onTrackProp={onTrackProp}
            selectedBooks={selectedBooks}
            eventId={eventId}
            eventName={eventName}
            playerInfo={playerInfo}
          />
        ))}

        {displayPlayers.length === 0 && (
          <div className="props-empty">No player props available</div>
        )}
      </div>
    </div>
  );
}

export default PropsSection;
